'use client';
/**
 * `cqi-lire` — lecture assistée d'une carte de contrôle interne (lot GED-13).
 *
 * ── La règle qui gouverne tout ce fichier ───────────────────────────────────
 *
 * L'ÉTAT DE LA CARTE EST CALCULÉ, PAS INTERPRÉTÉ. Les règles de Westgard sont
 * déterministes ; le rejet, l'alerte et la conformité sortent d'une fonction
 * unique et partagée côté moteur (`etatCarteCqi`). Une lecture assistée qui
 * paraîtrait trancher la même question serait un piège pour le technicien :
 * deux verdicts à l'écran, dont un seul fait foi, et rien pour dire lequel.
 *
 * Trois dispositions le rendent impossible à confondre :
 *
 *  1. Le VERDICT CALCULÉ est affiché en tête du panneau, dans ses couleurs
 *     habituelles, avec les codes de règles réellement déclenchés. Il est là
 *     avant qu'on ait demandé quoi que ce soit, et il y reste après.
 *  2. La lecture assistée est rendue en dessous, sur fond neutre, sans aucune
 *     pastille d'état et sans jamais reprendre le vocabulaire du verdict. Le
 *     catalogue serveur y veille aussi : le schéma de `cqi-lire` ne comporte
 *     AUCUN champ « conforme / rejet » — il ne peut pas en produire un.
 *  3. Quand la tendance lue et le verdict calculé pointent dans des directions
 *     différentes — série « stable » sur une carte en rejet, dérive lue sur une
 *     carte conforme — l'écran le DIT, et redit lequel des deux fait foi. Un
 *     désaccord est une information utile ; le taire serait le laisser agir.
 *
 * ── Ce qui s'écrit ──────────────────────────────────────────────────────────
 *
 * Rien. Ce panneau n'appelle aucune route d'écriture. Le seul pont vers le
 * dossier est « Reprendre dans le commentaire » : il REMPLIT le champ de
 * commentaire du passage sélectionné, préfixé de son origine, sans l'envoyer.
 * L'enregistrement reste le bouton « Enregistrer le commentaire » déjà présent,
 * c'est-à-dire un second geste, pris en connaissance du texte.
 */
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import type { EchecIA } from '@/lib/store';
import { I, MarqueIA, boutonIA, cadreIA, useBusy } from '@/components/ui';
import { useDisponibiliteIA, IndisponibiliteIA, EchecAssistanceIA, RappelProposition } from '@/components/AssistanceIA';
import type { Bloc, Point } from '@/components/screens/ressources/Cqi';

import { Bouton } from '@/shared/Bouton';
import { jourIso } from '@/lib/meta';
/** Ce que `cqi-lire` rend. Tout facultatif, pour la même raison que la trame :
 *  le schéma est imposé au serveur d'inférence, l'écran ne parie pas dessus. */
interface Lecture {
  lecture?: unknown; tendance?: unknown;
  pistes?: unknown; verifications?: unknown; remarque?: unknown;
}
const txt = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const liste = (v: unknown): string[] => (Array.isArray(v) ? v.map(txt).filter(Boolean) : []);

/** Les cinq tendances du schéma, en français, et SANS mot de verdict : ni
 *  « conforme », ni « rejet », ni « acceptable ». Une tendance décrit la forme
 *  du nuage de points ; elle ne dit pas si la série passe. */
const TENDANCES: Record<string, string> = {
  stable: 'nuage stable autour de la cible',
  derive: 'dérive — glissement progressif',
  decalage: 'décalage — saut de niveau',
  dispersion: 'dispersion croissante',
  indetermine: 'indéterminée — trop peu d’éléments',
};

/** Au-delà, on n'envoie pas la série entière : la lecture porte sur une
 *  fenêtre, et le moteur borne l'entrée à 8 000 caractères de toute façon. */
const MAX_POINTS = 60;

/**
 * La série mise en mots pour le moteur. Uniquement des chiffres de contrôle :
 * date, valeurs, écart en σ, verdict CALCULÉ et codes de règles. Aucune donnée
 * de patient n'entre ici — une carte de CQI n'en porte pas, et l'opérateur
 * lui-même n'est pas transmis : son nom n'aide en rien à lire une courbe.
 */
function serieEnTexte(blocs: Bloc[]): string {
  const points = blocs.flatMap((b) => b.points.map((p) => ({ p, lot: b.lotControle })));
  return points.slice(-MAX_POINTS).map(({ p, lot }) => {
    const vals = p.valeurs
      .map((v) => `N${v.niveau}=${v.valeur}${typeof v.z === 'number' ? ` (${v.z >= 0 ? '+' : ''}${v.z.toFixed(2)}σ)` : ''}`)
      .join(' ');
    const regles = p.regles.length ? ` règles=${p.regles.map((r) => r.code).join(',')}` : '';
    return `${jourIso(p.date)} lot=${lot || '—'} ${vals} calcul=${p.statut}${regles}`;
  }).join('\n');
}

const TONS: Record<string, { bg: string; fg: string; label: string }> = {
  conforme: { bg: '#E7F5EC', fg: 'var(--green-600)', label: 'Conforme' },
  alerte: { bg: '#FCF2E1', fg: 'var(--amber-600)', label: 'Avertissement' },
  rejet: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'Rejet' },
};

export function LectureCqi({ carte, blocs, selection, onReprendreCommentaire }: {
  carte: { analyte: string; analyteNom: string; equipement: string; statut: string; libellesNiveaux: string[]; reglesActives: string[] };
  blocs: Bloc[];
  selection: Point | null;
  /** Remplit le champ de commentaire du passage sélectionné. N'enregistre RIEN :
   *  c'est l'écran appelant qui garde le bouton d'enregistrement. */
  onReprendreCommentaire: (texte: string) => void;
}) {
  const { assisterIA } = useGed();
  const dispo = useDisponibiliteIA('cqi-lire');
  const [lecture, setLecture] = useState<Lecture | null>(null);
  const [echec, setEchec] = useState<{ motif: EchecIA; message: string } | null>(null);
  const { run, estOccupe } = useBusy();

  const points = blocs.flatMap((b) => b.points);
  // Les règles RÉELLEMENT déclenchées sur la fenêtre affichée — c'est ce que le
  // calcul a trouvé, et c'est ce qu'on transmet. Pas les règles paramétrées :
  // dire au moteur qu'une règle est active alors qu'elle n'a rien déclenché
  // l'inviterait à chercher un problème qui n'existe pas.
  const reglesDeclenchees = [...new Set(points.flatMap((p) => p.regles.map((r) => r.code)))];
  const ton = TONS[carte.statut] || TONS.conforme;

  const demander = () => run('lire', async () => {
    setEchec(null);
    const res = await assisterIA('cqi-lire', serieEnTexte(blocs), {
      analyte: `${carte.analyteNom} (${carte.analyte}) sur ${carte.equipement}`,
      niveau: carte.libellesNiveaux.filter(Boolean).join(' / '),
      regles: reglesDeclenchees.length
        ? `${reglesDeclenchees.join(', ')} — verdict calculé de la carte : ${carte.statut}`
        : `aucune — verdict calculé de la carte : ${carte.statut}`,
    }, { silencieux: true });
    if (res.ok) { setLecture(res.suggestion as Lecture); return; }
    setLecture(null);
    setEchec({ motif: res.motif, message: res.message });
  });

  const tendance = lecture ? txt(lecture.tendance) : '';
  /* Désaccord APPARENT entre la forme lue et l'état calculé. Ce n'est pas une
     erreur du moteur : une carte peut être en rejet sur un point isolé tout en
     ayant un nuage par ailleurs stable. Mais c'est exactement le moment où un
     technicien pressé pourrait retenir la mauvaise des deux phrases. */
  const desaccord = !!tendance && (
    (tendance === 'stable' && carte.statut === 'rejet')
    || (['derive', 'decalage', 'dispersion'].includes(tendance) && carte.statut === 'conforme')
  );

  const enCommentaire = () => {
    if (!lecture) return '';
    const pistes = liste(lecture.pistes);
    const verifs = liste(lecture.verifications);
    return [
      'Lecture proposée par l’assistance, relue avant enregistrement.',
      `Le verdict de la carte reste celui calculé par l’application : ${ton.label.toLowerCase()}${reglesDeclenchees.length ? ` (${reglesDeclenchees.join(', ')})` : ''}.`,
      txt(lecture.lecture),
      pistes.length ? `Pistes à vérifier : ${pistes.join(' ; ')}.` : '',
      verifs.length ? `Vérifications : ${verifs.join(' ; ')}.` : '',
      txt(lecture.remarque) ? `Réserve : ${txt(lecture.remarque)}` : '',
    ].filter(Boolean).join('\n');
  };

  const titre: React.CSSProperties = {
    fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
  };

  return (
    <section style={{ display: 'grid', gap: 10, padding: '12px 13px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}
      aria-label="Lecture assistée de la carte">
      {/* 1. LE CALCUL D'ABORD. Il est là que l'assistance soit disponible ou
             non, demandée ou non : c'est lui la référence, pas un complément. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ ...titre, marginBottom: 0 }}>Verdict calculé — fait foi</span>
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: ton.bg, color: ton.fg }}>
          {ton.label}
        </span>
        {reglesDeclenchees.length > 0 ? (
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-body)' }}>
            {reglesDeclenchees.join(' · ')}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>aucune règle déclenchée</span>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          · {points.length} point(s) sur la fenêtre affichée
        </span>
      </div>

      <div style={{ height: 1, background: 'var(--border-subtle)' }} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ ...titre, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
          <MarqueIA s={12} />Lecture assistée · facultative
        </span>
        {dispo.utilisable && (
          <Bouton taille="sm" style={boutonIA} onClick={() => void demander()} disabled={estOccupe() || points.length === 0}>
            <MarqueIA s={13} />{estOccupe('lire') ? 'Lecture en cours…' : lecture ? 'Relire la série' : 'Demander une lecture'}
          </Bouton>
        )}
      </div>

      <RappelProposition>
        L’assistance ne recalcule rien et ne peut pas lever un rejet : elle commente la forme de la
        série et propose des vérifications. Le rejet, l’alerte et la conformité restent ceux
        calculés ci-dessus.
      </RappelProposition>

      {!dispo.utilisable && <IndisponibiliteIA dispo={dispo} />}
      {dispo.utilisable && points.length === 0 && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun point sur cette fenêtre : il n’y a rien à lire.
        </span>
      )}
      {echec && <EchecAssistanceIA echec={echec} />}

      {lecture && (
        <div style={{ display: 'grid', gap: 9, ...cadreIA, borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
          {tendance && (
            <div>
              <div style={titre}>Tendance lue — description, pas verdict</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{TENDANCES[tendance] || tendance}</div>
            </div>
          )}

          {/* 3. Le désaccord est nommé, et l'arbitre rappelé. */}
          {desaccord && (
            <div style={{ display: 'grid', gap: 3, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50, #FFF8EC)', border: '1px solid var(--amber-300, #EFC77B)' }}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-strong)' }}>
                <I n="alert" s={13} />La lecture et le calcul ne disent pas la même chose
              </strong>
              <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
                C’est le calcul qui fait foi : la carte reste <strong>{ton.label.toLowerCase()}</strong>.
                La lecture décrit la forme du nuage, elle ne l’évalue pas — un point isolé peut faire
                rejeter une série par ailleurs régulière, et l’inverse existe aussi.
              </span>
            </div>
          )}

          {txt(lecture.lecture) && (
            <div>
              <div style={titre}>Ce que la série montre</div>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>{txt(lecture.lecture)}</p>
            </div>
          )}

          {liste(lecture.pistes).length > 0 && (
            <div>
              <div style={titre}>Pistes à vérifier — hypothèses, pas causes</div>
              <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 3 }}>
                {liste(lecture.pistes).map((p) => (
                  <li key={p} style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          {liste(lecture.verifications).length > 0 && (
            <div>
              <div style={titre}>À vérifier sur la paillasse</div>
              <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 3 }}>
                {liste(lecture.verifications).map((v) => (
                  <li key={v} style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{v}</li>
                ))}
              </ul>
            </div>
          )}

          {txt(lecture.remarque) && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Réserve de l’assistance : {txt(lecture.remarque)}
            </div>
          )}

          {/* Le seul pont vers le dossier — et il ne franchit pas le pont tout
              seul : il REMPLIT le champ, l'enregistrement reste au-dessus. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--border-subtle)', paddingTop: 8 }}>
            <Bouton taille="sm" onClick={() => onReprendreCommentaire(enCommentaire())} disabled={!selection}>
              <I n="pen" s={13} />Reprendre dans le commentaire
            </Bouton>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: '1 1 220px', lineHeight: 1.45 }}>
              {selection
                ? 'Le texte est déposé dans le champ de commentaire du passage sélectionné, avec son origine. Il n’est enregistré qu’au bouton « Enregistrer le commentaire ».'
                : 'Sélectionnez un point de la carte pour pouvoir reprendre cette lecture dans son commentaire.'}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
